import clsx from "clsx";
import React from "react";
import { MEStore } from "../MappingEditorStore";
import DynamicTable from "features/DynamicTable";
import { bimViewerStore } from "store/BimViewerStore";
import { useParams } from "react-router";
import useKeyPressed from "features/DynamicTable/components/useKeyPressed";
import { ColumnDef, Row } from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import BulkActionBar from "./BulkActionBar/BulkActionBar";
import { SingleItem } from "api-client";
import {
  dynamicTableStore,
  TableMode,
} from "features/DynamicTable/DynamicTableStore";
import useTableColumns from "../useTableColumns";

const METableView = () => {
  const { ifc_id } = useParams();
  const keyPressedLogic = useKeyPressed();

  function updateSelectedGroupsGlobalIfcIDs() {
    if (!MEStore.open3DViewer) return;
    if (
      MEStore.selectedRows.length &&
      dynamicTableStore.filterStore.hasGrouping()
    ) {
      MEStore.fetchGroupsGlobalIfcIds(ifc_id);
    } else {
      MEStore.setSelectedGroupsGlobalIDs([]);
    }
  }

  function update3DViewerWithGroupsHovering() {
    if (!MEStore.open3DViewer) return;
    const hoveredIDs = MEStore.hoveredRow
      ? MEStore.hoveredGroupsGlobalIDs
      : undefined;

    bimViewerStore.setHovered(
      hoveredIDs,
      undefined,
      MEStore.selectedRows.length > 0
    );
  }

  const handleOnClickRow = (row: Row<unknown>) => {
    if (dynamicTableStore.filterStore.hasGrouping()) return;
    if (MEStore.keyPressed === "Ctrl") {
      if (MEStore.selectedRowIndexes.includes(String(row.index))) {
        MEStore.removeFromSelectedRows(String(row.index));
      } else {
        MEStore.addToSelectedRows([String(row.index)]);
      }
    } else if (MEStore.keyPressed === "Shift") {
      keyPressedLogic.onPressedShift(row.index);
    } else {
      MEStore.setCurrentRow(row);
      MEStore.setOpenedObjectDetails(true);
    }
  };

  React.useEffect(() => {
    updateSelectedGroupsGlobalIfcIDs();
  }, [MEStore.selectedRows, MEStore.open3DViewer]);

  React.useEffect(() => {
    update3DViewerWithGroupsHovering();
  }, [MEStore.hoveredGroupsGlobalIDs, MEStore.hoveredRow]);

  const handleOnHoverRow = (row: Row<unknown> | null) => {
    if (!MEStore.open3DViewer) return;
    MEStore.setHoveredRow(row);
    const currentRow = row?.original as SingleItem;
    if (dynamicTableStore.filterStore.hasGrouping()) {
      row && MEStore.fetchGroupsGlobalIfcIds(ifc_id, currentRow, true);
      !row && MEStore.setHoveredGroupsGlobalIDs([]);
    } else {
      // clear any previous selection if the user removed grouping
      if (MEStore.hoveredGroupsGlobalIDs) {
        MEStore.setHoveredGroupsGlobalIDs([]);
      }
      bimViewerStore.setHovered(
        row ? [currentRow?.ifc_global_id as string] : undefined,
        (MEStore.currentRow?.original as SingleItem)?.ifc_global_id,
        MEStore.selectedRows.length > 0
      );
    }
  };

  const handleRowSelect = (e: string[]) => MEStore.setSelectedRows(e);

  const getColumns = () => {
    return useTableColumns() as ColumnDef<unknown, unknown>[];
  };

  const getData = async (start: number, _: number, update?: boolean) => {
    await dynamicTableStore.fetchObjectsList(start, update);
  };

  return (
    <div
      className={clsx(
        "flex flex-col items-center w-full",
        MEStore.open3DViewer ? "h-[calc(100%-18px)]" : "h-full"
      )}
    >
      <DynamicTable
        mode={TableMode.MappingEditor}
        getColumns={getColumns}
        onClickRow={handleOnClickRow}
        onKeyPressed={keyPressedLogic.handleKeyDown}
        onMouseDragged={keyPressedLogic.handleMouseDragged}
        onHoverRow={handleOnHoverRow}
        getData={getData}
        onRowSelect={handleRowSelect}
        disableUserInteraction={MEStore.interactionDisabled}
        selectedRows={MEStore.selectedRowIndexes}
        selectAllChecked={MEStore.selectAllChecked}
        currentRowID={String((MEStore.currentRow?.original as SingleItem)?.id)}
        columnStyles="[&:nth-of-type(1)]:px-0"
      />

      <BulkActionBar />
    </div>
  );
};

export default observer(METableView);
